import React from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="effective-date">Effective Date: March 1, 2025</p>
        </div>

        <div className="privacy-content">
          <section className="privacy-section">
            <p className="intro-text">
              At Jobque.ai ("Company," "we," "our," or "us"), we prioritize your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, applications, products, and services ("Services").
            </p>
          </section>

          <section className="privacy-section">
            <h2>Information We Collect</h2>
            <p>We collect information that you provide directly to us and information collected automatically:</p>
            
            <h3>Personal Information</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, and profile information when you create an account</li>
              <li><strong>Contact Information:</strong> Information you provide when contacting our support team</li>
              <li><strong>Payment Information:</strong> Payment details processed securely through Stripe (we do not store your full payment card details)</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li><strong>Usage Data:</strong> Information about how you interact with our Services, including pages visited, features used, and time spent</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, and IP address</li>
              <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to enhance your experience</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our Services</li>
              <li>Process transactions and send related information</li>
              <li>Send you job alerts and notifications based on your preferences</li>
              <li>Communicate with you about products, services, and updates</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Sharing Your Information</h2>
            <p>We do not sell your personal information. We may share your information with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party vendors who assist in providing our Services (e.g., Stripe for payment processing, Google Analytics for website analytics)</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you have given us permission to share your information</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate personal data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Object:</strong> Object to processing of your personal data</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p>
              To exercise these rights, please contact us at{' '}
              <a href="mailto:legal@jobque.ai">legal@jobque.ai</a>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide our Services and fulfill the purposes described in this Privacy Policy, unless a longer retention period is required by law. When you delete your account, we will delete or anonymize your personal information within 30 days.
            </p>
          </section>

          <section className="privacy-section">
            <h2>International Data Transfers</h2>
            <p>
              We operate globally and may transfer your information to countries outside your country of residence, including Canada and the United States. By using our Services, you consent to the transfer of information to countries that may have different data protection rules. We ensure appropriate safeguards are in place to protect your information.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Children's Privacy</h2>
            <p>
              Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we learn that we have collected personal information from a child under 18, we will take steps to delete that information.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Cookies and Tracking</h2>
            <p>
              We use cookies and similar tracking technologies to collect information about your browsing activities. You can control cookies through your browser settings. Please see our <Link to="/settings">Cookie Policy</Link> for more details.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Third-Party Links</h2>
            <p>
              Our Services may contain links to third-party websites and services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date." Your continued use of our Services after any changes indicates your acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>California Privacy Rights (CCPA)</h2>
            <p>
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul>
              <li>Right to know what personal information is collected, used, shared, or sold</li>
              <li>Right to delete personal information held by businesses</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to non-discrimination for exercising your CCPA rights</li>
            </ul>
            <p>
              We do not sell personal information as defined by the CCPA.
            </p>
          </section>

          <section className="privacy-section">
            <h2>European Privacy Rights (GDPR)</h2>
            <p>
              If you are located in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR), including the rights described above. Our legal basis for processing your personal data includes contract performance, legitimate interests, and consent.
            </p>
          </section>

          <section className="privacy-section contact-section">
            <h2>Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <div className="contact-info">
              <p><strong>Jobque.ai</strong></p>
              <p>Email: <a href="mailto:legal@jobque.ai">legal@jobque.ai</a></p>
              <p>Website: <a href="https://jobque.ai">jobque.ai</a></p>
            </div>
          </section>

          <div className="privacy-footer-links">
            <Link to="/settings" className="footer-link">Terms and Conditions</Link>
            <span className="divider">|</span>
            <Link to="/settings" className="footer-link">Cookie Policy</Link>
            <span className="divider">|</span>
            <Link to="/" className="footer-link">Back to Home</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;